/**
 * Balance Sheet Panel — group-level snapshot with XLSX upload.
 *
 * Reads:
 *   GET /myob/balance-sheet/periods    → list of loaded period_end dates
 *   GET /myob/balance-sheet/snapshot   → sections + accounts + totals + prior-period movement
 * Writes:
 *   POST /myob/balance-sheet/import    → upload a period XLSX
 *   DELETE /myob/balance-sheet/period/{period_end}
 *
 * Design:
 *   • Compact KPI strip at top: Assets total, Liabilities total, Equity total,
 *     Net-Assets check (should be zero if Assets = Liab + Equity)
 *   • Grouped table per section (Assets → Liabilities → Equity) with expandable
 *     subsections and per-account movement vs prior period.
 */
import { useEffect, useMemo, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, RefreshCw, AlertCircle } from "lucide-react";

const SECTION_META = {
  asset:     { label: "Assets",      color: "emerald" },
  liability: { label: "Liabilities", color: "rose"    },
  equity:    { label: "Equity",      color: "indigo"  },
  other:     { label: "Other",       color: "slate"   },
};

const _sectionCls = (color) => ({
  border: `border-${color}-200`,
  bg: `bg-${color}-50`,
  text: `text-${color}-800`,
  totalBg: `bg-${color}-100`,
});

export default function BalanceSheetPanel({ canEdit = false }) {
  const [periods, setPeriods] = useState([]);
  const [asAt, setAsAt] = useState("");
  const [snap, setSnap] = useState(null);
  const [trend, setTrend] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expandedSubs, setExpandedSubs] = useState({});   // {section: {sub: bool}}

  const loadPeriods = async () => {
    try {
      const { data } = await api.get("/myob/balance-sheet/periods");
      setPeriods(data.periods || []);
      // Auto-select newest if nothing chosen yet
      if (!asAt && (data.periods || []).length) {
        setAsAt(data.periods[0].period_end);
      }
    } catch (e) {
      toast.error("Failed to load Balance Sheet periods");
    }
  };

  const loadTrend = async () => {
    try {
      const { data } = await api.get("/myob/balance-sheet/trend");
      setTrend(data);
    } catch (e) { /* silent — trend is a nice-to-have */ }
  };

  const loadSnap = async (period) => {
    setBusy(true);
    try {
      const { data } = await api.get("/myob/balance-sheet/snapshot", {
        params: period ? { as_at: period } : {},
      });
      setSnap(data);
      if (data.as_at && data.as_at !== asAt) setAsAt(data.as_at);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load snapshot");
    } finally { setBusy(false); }
  };

  useEffect(() => { loadPeriods(); loadTrend(); }, []);
  useEffect(() => {
    // Load snap when asAt changes (or first mount if no periods yet — the API
    // will still return {note: "No Balance Sheet data loaded."})
    loadSnap(asAt || undefined);
  }, [asAt]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/myob/balance-sheet/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.rows_upserted} rows for ${data.period_end}`);
      (data.warnings || []).forEach((w) => toast.warning(w));
      await loadPeriods();
      await loadTrend();
      setAsAt(data.period_end);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDeletePeriod = async () => {
    if (!asAt) return;
    if (!window.confirm(`Delete Balance Sheet data for ${asAt}? This cannot be undone.`)) return;
    try {
      await api.delete(`/myob/balance-sheet/period/${asAt}`);
      toast.success(`Deleted period ${asAt}`);
      await loadPeriods();
      await loadTrend();
      // Auto-select newest remaining
      setAsAt("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const toggleSub = (section, subsection) => {
    setExpandedSubs((prev) => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [subsection]: !prev[section]?.[subsection] },
    }));
  };

  const noData = !snap?.as_at;

  return (
    <div className="space-y-4" data-testid="balance-sheet-panel">
      {/* Header row: period picker + upload + delete */}
      <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-lg p-3">
        <div className="flex-1 min-w-[200px]">
          <div className="font-semibold text-slate-800 text-sm">Balance Sheet</div>
          <div className="text-[11px] text-slate-500">
            Group snapshot at period-end. Upload a MYOB Balance Sheet XLSX for each period-end you want to track.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {periods.length > 0 && (
            <Select value={asAt} onValueChange={setAsAt}>
              <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="bs-period-select">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                {periods.map((p) => (
                  <SelectItem key={p.period_end} value={p.period_end}>
                    {p.period_end} · {p.row_count} rows
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button size="sm" variant="outline" onClick={() => loadSnap(asAt)}
                  disabled={busy} className="text-xs" data-testid="bs-refresh-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
          {canEdit && (
            <>
              <label className="inline-flex items-center">
                <input
                  type="file" accept=".xlsx"
                  onChange={handleUpload} className="sr-only"
                  data-testid="bs-upload-input"
                />
                <span className={`inline-flex items-center gap-1 px-3 h-8 rounded-md border text-xs cursor-pointer
                                  ${uploading ? "bg-slate-100 text-slate-400 border-slate-200"
                                              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  {uploading ? "Uploading…" : "Upload XLSX"}
                </span>
              </label>
              {asAt && (
                <Button size="sm" variant="outline"
                        onClick={handleDeletePeriod}
                        className="text-xs text-rose-700 border-rose-200 hover:bg-rose-50"
                        data-testid="bs-delete-period-btn"
                        title={`Delete Balance Sheet for ${asAt}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {noData ? (
        <div className="bg-white border border-slate-200 rounded-lg p-8 text-center"
             data-testid="bs-empty-state">
          <AlertCircle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
          <div className="text-slate-700 font-medium">No Balance Sheet data loaded</div>
          <div className="text-xs text-slate-500 mt-1">
            Upload a MYOB Balance Sheet XLSX to populate this dashboard.
          </div>
        </div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiCard label="Assets" amount={snap.assets_total} tone="emerald"
                     testid="bs-kpi-assets" />
            <KpiCard label="Liabilities" amount={snap.liabilities_total} tone="rose"
                     testid="bs-kpi-liabilities" />
            <KpiCard label="Equity" amount={snap.equity_total} tone="indigo"
                     testid="bs-kpi-equity" />
            <KpiCard label="Net-Assets check"
                     amount={snap.net_assets_check}
                     tone={Math.abs(snap.net_assets_check) < 1 ? "slate" : "amber"}
                     testid="bs-kpi-check"
                     hint={Math.abs(snap.net_assets_check) < 1
                       ? "Assets = Liabilities + Equity (balanced)"
                       : "⚠ Balance-sheet identity does not hold — check imported rows"} />
          </div>

          <div className="text-[11px] text-slate-500 flex items-center gap-4"
               data-testid="bs-provenance">
            <span>As-at: <b>{snap.as_at}</b></span>
            {snap.prior_period && <span>Prior: <b>{snap.prior_period}</b></span>}
            <span>{snap.coverage?.account_count || 0} accounts</span>
          </div>

          {/* Trend across periods */}
          <BSTrendChart trend={trend} />

          {/* Sections */}
          {(snap.sections || []).map((sect) => {
            const meta = SECTION_META[sect.section] || SECTION_META.other;
            return (
              <div key={sect.section}
                   className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                   data-testid={`bs-section-${sect.section}`}>
                <div className={`px-4 py-2 flex items-center justify-between bg-${meta.color}-50 border-b border-${meta.color}-100`}>
                  <div className={`font-semibold text-${meta.color}-900`}>
                    {meta.label}
                  </div>
                  <div className={`text-${meta.color}-900 font-semibold tabular-nums`}>
                    {fmtCurrency(sect.total)}
                  </div>
                </div>
                <div>
                  {(sect.subsections || []).map((sub) => {
                    const isOpen = expandedSubs[sect.section]?.[sub.subsection] !== false;   // default open
                    return (
                      <div key={sub.subsection} className="border-b last:border-b-0 border-slate-100">
                        <button
                          type="button"
                          onClick={() => toggleSub(sect.section, sub.subsection)}
                          className="w-full px-4 py-2 flex items-center justify-between hover:bg-slate-50 text-left"
                          data-testid={`bs-subsection-${sect.section}-${sub.subsection}`}
                        >
                          <span className="text-xs uppercase tracking-wider text-slate-600 font-medium">
                            {isOpen ? "▾" : "▸"} {sub.subsection.replace(/_/g, " ")}
                            <span className="ml-2 text-slate-400 normal-case">
                              ({(sub.accounts || []).length} accounts)
                            </span>
                          </span>
                          <span className="text-sm text-slate-800 tabular-nums">
                            {fmtCurrency(sub.total)}
                          </span>
                        </button>
                        {isOpen && (sub.accounts || []).length > 0 && (
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-slate-500 text-[10px] uppercase tracking-wider border-b border-slate-100 bg-slate-50/60">
                                <th className="text-left px-4 py-1 pl-8">Account</th>
                                <th className="text-right px-4 py-1">This period</th>
                                <th className="text-right px-4 py-1">Prior</th>
                                <th className="text-right px-4 py-1 pr-6">Movement</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sub.accounts.map((a) => {
                                const mv = a.movement;
                                const mvCls = mv == null ? "text-slate-400"
                                              : mv > 0 ? "text-emerald-700"
                                              : mv < 0 ? "text-rose-700"
                                              : "text-slate-500";
                                return (
                                  <tr key={a.account_no + a.account_name}
                                      className="border-b last:border-b-0 border-slate-50 hover:bg-slate-50/50">
                                    <td className="px-4 py-1 pl-8">
                                      <span className="font-mono text-[10px] text-slate-400">
                                        {a.account_no}
                                      </span>{" "}
                                      <span className="text-slate-700">{a.account_name}</span>
                                    </td>
                                    <td className="text-right px-4 py-1 tabular-nums text-slate-800">{fmtCurrency(a.amount)}</td>
                                    <td className="text-right px-4 py-1 tabular-nums text-slate-500">
                                      {a.prior_amount != null ? fmtCurrency(a.prior_amount) : "—"}
                                    </td>
                                    <td className={`text-right px-4 py-1 pr-6 tabular-nums ${mvCls}`}>
                                      {mv != null ? (mv > 0 ? "+" : "") + fmtCurrency(mv) : "—"}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}


function KpiCard({ label, amount, tone, testid, hint }) {
  const cls = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    slate: "bg-slate-50 border-slate-200 text-slate-800",
  }[tone] || "bg-slate-50 border-slate-200 text-slate-800";
  return (
    <div className={`rounded-lg border p-3 ${cls}`} data-testid={testid} title={hint || undefined}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-semibold tabular-nums mt-1">{fmtCurrency(amount)}</div>
      {hint && <div className="text-[10px] opacity-60 mt-1 truncate">{hint}</div>}
    </div>
  );
}


// ============================================================
// BSTrendChart — Cross-period trend of Net Assets + Cash
// ============================================================
function BSTrendChart({ trend }) {
  const rows = trend?.periods || [];
  if (rows.length < 2) {
    // Need at least 2 uploaded periods to draw a meaningful trend
    return null;
  }

  const W = 720, H = 200, pad = { l: 60, r: 20, t: 20, b: 34 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;

  const values = rows.flatMap((r) => [r.net_assets, r.cash_total]);
  const maxV = Math.max(0, ...values);
  const minV = Math.min(0, ...values);
  const span = maxV - minV || 1;

  const x = (i) => pad.l + (i * innerW) / Math.max(1, rows.length - 1);
  const y = (v) => pad.t + innerH - ((v - minV) / span) * innerH;

  const line = (key) =>
    rows.map((r, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(r[key])}`).join(" ");

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3"
         data-testid="bs-trend-chart">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-semibold text-slate-800 text-sm">Trend across periods</div>
          <div className="text-[11px] text-slate-500">
            Net Assets = Assets − Liabilities.
            Cash = sum of Cheque, Bank, Petty Cash, Term Deposit, and Clearing accounts.
          </div>
        </div>
        <div className="flex gap-3 text-[11px]">
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-indigo-600"></span>
            Net Assets
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-emerald-500"></span>
            Cash
          </span>
        </div>
      </div>
      <svg width={W} height={H} className="w-full max-w-full">
        {/* Y-axis gridlines (4 ticks) */}
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const yVal = minV + t * span;
          return (
            <g key={t}>
              <line x1={pad.l} x2={W - pad.r}
                    y1={y(yVal)} y2={y(yVal)}
                    stroke="#f1f5f9" strokeWidth="1" />
              <text x={pad.l - 6} y={y(yVal)} dy="3"
                    textAnchor="end" className="fill-slate-400 text-[10px]">
                {fmtK(yVal)}
              </text>
            </g>
          );
        })}
        {/* Zero line */}
        {minV < 0 && maxV > 0 && (
          <line x1={pad.l} x2={W - pad.r}
                y1={y(0)} y2={y(0)}
                stroke="#94a3b8" strokeWidth="1" strokeDasharray="3,3" />
        )}
        {/* Cash line */}
        <path d={line("cash_total")}
              fill="none" stroke="#10b981" strokeWidth="2" />
        {/* Net Assets line */}
        <path d={line("net_assets")}
              fill="none" stroke="#4f46e5" strokeWidth="2" />
        {/* Points + hover values */}
        {rows.map((r, i) => (
          <g key={r.period_end}>
            <circle cx={x(i)} cy={y(r.cash_total)} r="3" fill="#10b981" />
            <circle cx={x(i)} cy={y(r.net_assets)} r="3" fill="#4f46e5" />
            <text x={x(i)} y={H - pad.b + 14}
                  textAnchor="middle" className="fill-slate-500 text-[10px]">
              {r.period_end.slice(0, 7)}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

// Local helper — abbreviated $ formatter used by the SVG trend chart labels.
function fmtK(n) {
  if (n == null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return "$" + (n / 1_000_000).toFixed(1) + "M";
  if (abs >= 1_000) return "$" + Math.round(n / 1_000) + "K";
  return "$" + Math.round(n);
}

